'use client';

import React, { useCallback } from 'react';
import { ICamera } from '@/data/interfaces/camera';
import IntegrationsThingsTalkdownComponent from '@/components/queue/IntegrationsThingsTalkdownComponent';
import { useTalkdownData } from '@/hooks/useTalkdownData';

interface CameraControlsWinBoxProps {
  cameraId: string;
  cameraData: ICamera;
  alertId?: string;
  locationId?: number;
  onClose?: () => void;
}

const CameraControlsWinBox: React.FC<CameraControlsWinBoxProps> = ({
  cameraId,
  cameraData,
  alertId,
  locationId,
  onClose
}) => {
  const { things, cameras, isLoading, error } = useTalkdownData({
    locationId: locationId || cameraData.LocationId,
    cameraId,
    context: 'cameras'
  });

  if (isLoading) {
    return (
      <div className="h-full bg-slate-800 text-white overflow-hidden flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-sm text-gray-300">Loading devices...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full bg-slate-800 text-white overflow-hidden flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-2">Error loading devices</p>
          <p className="text-xs text-gray-400">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-slate-800 text-white overflow-hidden">
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {locationId ? (
            <IntegrationsThingsTalkdownComponent
              alertId={alertId}
              locationId={locationId}
              things={things}
              cameras={cameras}
              cameraId={cameraId}
              allowFileUpload={true}
              showFileDropZone={true}
              shouldCreateNote={false}
            />
          ) : (
            <div className="space-y-4">
              <div className="text-center text-gray-400 py-4">
                <p className="text-lg font-semibold text-white mb-2">Camera Controls</p>
                <p className="text-sm">Camera: {cameraData.name}</p>
                <p className="text-xs text-gray-500 mt-2">Camera Player ID: {cameraData.cameraPlayerId}</p>
              </div>

              <div className="border-t border-slate-700 pt-4">
                <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-semibold text-red-300 mb-2">⚠️ Limited Functionality</h4>
                  <p className="text-xs text-red-200">
                    Location context is required for audio controls. Please use this feature from the alerts or queue pages.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-700 bg-slate-900">
          <div className="text-xs text-gray-500 text-center">
            <p>Camera Player ID: {cameraData.cameraPlayerId}</p>
            <p>Use keyboard shortcuts: R (record), 1 (precautionary), 2 (restricted)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CameraControlsWinBox;
